import { useEffect } from "react";
import { Link, NavLink, Outlet, useMatch, useNavigate, useParams } from "react-router-dom";
import { ApiRequestError } from "@mmm/contracts";
import { useQuery } from "../../api/useQuery.js";
import { useBackTo } from "../../routes/useBackTo.js";
import { getSubjectList, getSubjectOverview } from "../../api/queries.js";
import { LoadingRows, LoadingSkeleton, QueryStatus } from "../../components/ui/LoadingPlaceholders.js";
import { StudyWorkspaceActions } from "../../components/templates/StudyWorkspaceActions.js";
import { PageDateLine } from "../../components/ui/PageTitle.js";
import { recordRecentPage } from "../../recents.js";
import type { SubjectWithChildren } from "../../types.js";
import { ColorDot, ErrorState, Icon, type MessageKey, useT } from "@mmm/ui";
// Overview first, and the tab a bare /subjects/:id redirects to.
const TABS: Array<{ to: string; label: MessageKey; end?: boolean }> = [
  { to: "overview", label: "nav.overview" },
  { to: "map", label: "nav.map" },
  { to: "tasks", label: "nav.tasks" },
  { to: "calendar", label: "calendar.title" },
  { to: "tests", label: "nav.tests" },
];

export function SubjectLayout() {
  const t = useT();
  const { subjectId = "" } = useParams();
  const navigate = useNavigate();
  const backLink = useBackTo();
  const overview = useQuery(getSubjectOverview, { subjectId });
  // The subject list is primed at boot and cached, so the header + tabs can
  // render in the same frame as the navigation — only the tab content waits
  // for the overview payload. Keeps the shell anchored instead of blanking.
  const subjectList = useQuery(getSubjectList, undefined);
  // Exact match only: true for /subjects/:id, false for every tab under it.
  const atSubjectRoot = useMatch("/subjects/:subjectId") !== null;
  const atTests = useMatch("/subjects/:subjectId/tests") !== null;
  const atTestDocument = useMatch("/subjects/:subjectId/tests/:testId/*") !== null;

  useEffect(() => {
    if (overview.error instanceof ApiRequestError && overview.error.code === "notFound") {
      navigate("/dashboard", { replace: true });
    }
  }, [overview.error, navigate]);

  const summary = overview.data?.subject ?? subjectList.data?.find((s) => s.id === subjectId);

  // Record one subject-level recent entry, regardless of the active tab.
  const summaryTitle = summary?.title;
  const summaryColor = summary?.color;
  useEffect(() => {
    if (!summaryTitle || !summaryColor) return;
    recordRecentPage({
      path: `/subjects/${subjectId}`,
      title: summaryTitle,
      detail: t("common.subject"),
      color: summaryColor,
      subjectId,
    });
  }, [subjectId, summaryTitle, summaryColor, t]);

  const overviewNotFound = overview.error instanceof ApiRequestError && overview.error.code === "notFound";

  if (!summary) {
    // notFound is handled by the redirect effect above — don't flash an
    // ErrorState in the single render before it fires.
    if (overview.error && !overview.waiting && !overviewNotFound) {
      return (
        <div>
          <ErrorState title={t("subjects.loadOneFailed")} onRetry={overview.retry} />
        </div>
      );
    }
    return (
      <div>
        <div className="mmm-page-header">
          <div style={{ width: "min(420px, 100%)" }}>
            <LoadingSkeleton height={13} width="120px" />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <LoadingSkeleton height={20} width="20px" className="mmm-skeleton--swatch" />
              <LoadingSkeleton height={30} width="72%" />
            </div>
          </div>
        </div>
        <QueryStatus queries={[overview, subjectList]} />
        <div className="mmm-card" style={{ padding: 20, maxWidth: 640 }}>
          <LoadingRows rows={4} />
        </div>
      </div>
    );
  }

  const subject: SubjectWithChildren | null = overview.data
    ? {
        ...overview.data.subject,
        chapters: overview.data.chapters,
        checkItems: overview.data.checkItems,
        events: overview.data.events,
        mindMaps: overview.data.mindMaps,
        aiTests: overview.data.aiTests,
      }
    : null;

  // Archive/delete/rename live on the parent screens (subject cards on Home
  // and the Subject List, via the edit dialog) — this header is read-only.
  return (
    <div>
      <div className="mmm-page-header">
        <div>
          <PageDateLine />
          <div className="mmm-title-line">
            {backLink && (
              <Link className="mmm-title-back-btn" to={backLink.to} aria-label={backLink.label}>
                <Icon name="collapse" />
              </Link>
            )}
            <ColorDot color={summary.color} size="md" square />
            <h1 className="mmm-page-title">{summary.title}</h1>
          </div>
        </div>
      </div>
      <QueryStatus queries={[overview, subjectList]} />

      <div className="mmm-workspace-tabs-row">
        <div className="mmm-workspace-tabs">
          {TABS.map((tab) => (
            <NavLink
              key={tab.to || "overview"}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => `mmm-workspace-tab ${isActive ? "is-active" : ""}`.trim()}
            >
              {t(tab.label)}
            </NavLink>
          ))}
        </div>

        {subject && !atTestDocument && (
          <StudyWorkspaceActions key={subject.id} subject={subject} tests={atTests} />
        )}
      </div>

      {/* The index route only redirects to /overview and reads no context, so it
          must render before the subject loads. Gating it behind `subject`
          left a cold deep link sitting on the bare /subjects/:id URL, with no
          tab active, for as long as the overview query took. */}
      {atSubjectRoot ? <Outlet /> : subject && <Outlet context={{ subject }} />}
    </div>
  );
}
